/**
 * chatMessageUtils — pure helpers for the AI chat panel.
 *
 * The centrepiece is reconcileMessages(): the flash-free "finish" merge.
 * While a reply streams, the panel shows locally-keyed rows (`temp-*` user
 * message, `stream-N` assistant bubble). When the SSE stream ends, the panel
 * fetches the persisted conversation and merges it INTO the existing list —
 * reusing prior row object references (so memoized bubbles skip re-render)
 * and carrying local render keys onto the freshly-persisted rows (so React
 * never unmounts a bubble that is already on screen). No list clear, no
 * loading spinner, no scroll jump.
 */
import type { ChatMessage } from '../../services/chatService';
import apiClient from '../../services/apiClient';

/** Local (not-yet-persisted) render keys created by the panel. */
export function isLocalKey(key: string): boolean {
  return key.startsWith('temp-') || key.startsWith('stream-');
}

function normText(s: string | null | undefined): string {
  return (s ?? '').trim();
}

/** True when the two rows would render identically (safe to keep the old object). */
function rowEquivalent(a: ChatMessage, b: ChatMessage): boolean {
  return a.Role === b.Role
    && normText(a.Content) === normText(b.Content)
    && a.ToolName === b.ToolName
    && a.ToolInput === b.ToolInput
    && a.ToolOutput === b.ToolOutput
    && a.InputTokens === b.InputTokens
    && a.OutputTokens === b.OutputTokens
    && a.DurationMs === b.DurationMs;
}

export interface ReconcileOpts {
  /** Render key of the in-flight streamed assistant bubble (e.g. `stream-3`). */
  streamKey?: string;
  /** Full streamed text of this turn (may span multiple tool iterations). */
  streamText?: string;
}

/**
 * Merge the server's persisted rows into the currently-rendered list.
 *
 * Guarantees:
 * - Byte-identical existing rows keep their exact object reference → a
 *   React.memo'd renderer does not re-render them at all.
 * - A local `temp-*` row (the optimistic user message) that the server has
 *   now persisted adopts the DB fields but KEEPS its local MessageKey, so the
 *   rendered bubble is updated in place — never unmounted.
 * - The final streamed assistant row (matched as the last plain-assistant row
 *   whose content is a suffix of the streamed text) adopts `opts.streamKey` as
 *   its MessageKey — the streaming bubble morphs into the persisted bubble in
 *   a single commit with a stable key.
 * - Brand-new rows the client never displayed (tool calls / tool results) are
 *   appended with their DB keys and simply mount.
 * - If nothing changed at all, the ORIGINAL array reference is returned.
 */
export function reconcileMessages(
  prev: ChatMessage[],
  fetched: ChatMessage[],
  opts: ReconcileOpts = {},
): ChatMessage[] {
  if (fetched.length === 0) return prev;

  // Identify the persisted row that corresponds to the streamed bubble: the
  // LAST plain assistant row (no tool call) that is new relative to prev and
  // whose content matches the tail of the streamed text.
  let streamRowIdx = -1;
  const streamNorm = normText(opts.streamText);
  if (opts.streamKey && streamNorm) {
    for (let i = fetched.length - 1; i >= 0; i--) {
      const f = fetched[i];
      if (f.Role === 'assistant' && !f.ToolName && normText(f.Content)
        && streamNorm.endsWith(normText(f.Content))) {
        streamRowIdx = i;
        break;
      }
    }
  }

  const result: ChatMessage[] = [];
  let changed = fetched.length !== prev.length;
  let pi = 0;

  for (let fi = 0; fi < fetched.length; fi++) {
    const f = fetched[fi];
    const p = pi < prev.length ? prev[pi] : undefined;

    if (p && p.MessageKey === f.MessageKey) {
      // Same persisted row — keep the old reference when render-identical.
      if (rowEquivalent(p, f)) {
        result.push(p);
      } else {
        result.push(f);
        changed = true;
      }
      pi++;
      continue;
    }

    if (p && isLocalKey(p.MessageKey) && p.Role === f.Role
      && normText(p.Content) === normText(f.Content)) {
      // Optimistic local row now persisted — adopt DB fields, keep render key.
      if (rowEquivalent(p, f)) {
        result.push(p); // nothing visible changed; keep the old object
      } else {
        result.push({ ...f, MessageKey: p.MessageKey });
        changed = true;
      }
      pi++;
      continue;
    }

    // New row from the server (tool call / tool result / final answer).
    if (fi === streamRowIdx && opts.streamKey) {
      result.push({ ...f, MessageKey: opts.streamKey });
    } else {
      result.push(f);
    }
    changed = true;
  }

  // Any unmatched local leftovers in prev are dropped — the server is the
  // source of truth (errors surface via the panel's error bubble instead).
  if (pi < prev.length) changed = true;

  return changed ? result : prev;
}

// ── small display formatters ────────────────────────────────────────────────

/** "claude-sonnet-5-20260115" → "Sonnet 5"; unknown ids pass through shortened. */
export function modelShortName(model: string | null | undefined): string {
  if (!model) return 'Sonnet 5';
  const m = model.toLowerCase();
  const match = m.match(/claude-(opus|sonnet|haiku)-(\d+)(?:-(\d+))?/);
  if (match) {
    const family = match[1][0].toUpperCase() + match[1].slice(1);
    // claude-sonnet-4-5 → "Sonnet 4.5"; claude-sonnet-5-20260115 → "Sonnet 5"
    const minor = match[3] && match[3].length <= 2 ? `.${match[3]}` : '';
    return `${family} ${match[2]}${minor}`;
  }
  return model.replace(/^claude-/, '').split('-').slice(0, 2).join(' ');
}

/** 4321 → "4.3s"; 65000 → "1m 5s"; 800 → "0.8s". */
export function formatDuration(ms: number | null | undefined): string | null {
  if (!ms || ms <= 0) return null;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  const min = Math.floor(ms / 60_000);
  const sec = Math.round((ms % 60_000) / 1000);
  return sec > 0 ? `${min}m ${sec}s` : `${min}m`;
}

/** 12345 → "12.3k"; 987 → "987". */
export function formatTokens(n: number | null | undefined): string | null {
  if (!n || n <= 0) return null;
  return n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n);
}

/** Grouping key for day dividers (local calendar day). */
export function dayKey(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** "Today" / "Yesterday" / "Jul 11" (adds year when not the current year). */
export function formatDayLabel(iso: string, now: Date = new Date()): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const startOf = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diffDays = Math.round((startOf(now) - startOf(d)) / 86_400_000);
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  const opts: Intl.DateTimeFormatOptions = d.getFullYear() === now.getFullYear()
    ? { month: 'short', day: 'numeric' }
    : { month: 'short', day: 'numeric', year: 'numeric' };
  return d.toLocaleDateString('en-CA', opts);
}

/**
 * Rewrite relative /api/ links to the backend origin (avoids hitting the Vite
 * dev server). Shared by markdown links and CSV download cards.
 */
export function resolveApiHref(href: string): string;
export function resolveApiHref(href: string | undefined): string | undefined;
export function resolveApiHref(href: string | undefined): string | undefined {
  if (!href) return href;
  if (href.startsWith('/api/')) {
    const backendBase = import.meta.env.VITE_API_URL?.replace(/\/api$/, '') || 'http://localhost:8080';
    return `${backendBase}${href}`;
  }
  return href;
}

/** True for links served by our API — these need the Bearer token, so a raw
 *  browser navigation (new tab) fails with "Missing Authorization header". */
export function isApiHref(href: string | undefined): boolean {
  return !!href && href.startsWith('/api/');
}

/**
 * Fetch an API-served file (CSV export, report PDF) THROUGH the authenticated
 * axios client so the MSAL Bearer token is attached, then hand the blob to the
 * browser: attachments (CSV) download, everything else (PDF) opens in a new tab.
 * Fixes the "Missing Authorization header" error from opening /api links raw.
 */
export async function openAuthenticatedApiFile(apiPath: string): Promise<void> {
  // apiClient.baseURL already ends in /api, so drop the leading /api segment.
  const path = apiPath.replace(/^\/api(?=\/)/, '');
  const res = await apiClient.get(path, { responseType: 'blob' });
  const blob = res.data as Blob;

  const disposition = String(res.headers?.['content-disposition'] ?? '');
  const isAttachment = /attachment/i.test(disposition);
  const nameMatch = disposition.match(/filename\*?=(?:UTF-8''|")?([^";]+)/i);
  const filename = nameMatch ? decodeURIComponent(nameMatch[1].replace(/"$/, '')) : '';

  const url = URL.createObjectURL(blob);
  try {
    if (isAttachment) {
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'export';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } else {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  } finally {
    // Revoke after a tick so the new tab / download can start reading it.
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  }
}
